// Browser error reporting (Sentry), loaded only when a build asks for it.
//
// With REACT_APP_SENTRY_DSN unset -- development, tests, any build that has not
// opted in -- nothing here does anything, and the SDK is never downloaded: it
// lives in its own chunk (sentryClient.js) that only an import() below can
// pull in. A visitor on a slow connection pays for monitoring only on builds
// that use it, and even then after the page has started.
//
// Reports go to our own API (/api/monitoring/tunnel), not to sentry.io. The
// storefront's CSP allows connections to 'self' only, and the relay on the
// server is also where reports are scrubbed of customer data before anything
// leaves -- see backend/routes/monitoring.js.

import { API_BASE_URL } from './config';

const DSN = (process.env.REACT_APP_SENTRY_DSN || '').trim();

// Errors thrown while the SDK chunk is still downloading. Held briefly and
// replayed once it arrives, so a crash during start-up -- the likeliest time
// for a bad deploy to show itself -- is not the one thing never reported.
const MAX_EARLY = 10;
let early = [];
let client = null;
let started = false;

const hold = (error, context) => {
  if (early && early.length < MAX_EARLY) early.push([error, context]);
};

const onEarlyError = (event) => hold(event.error || new Error(event.message || 'Script error'));
const onEarlyRejection = (event) => hold(event.reason instanceof Error ? event.reason : new Error(String(event.reason)));

export const isMonitoringConfigured = () => Boolean(DSN);

export function initMonitoring() {
  if (!DSN || started || typeof window === 'undefined') return;
  started = true;

  window.addEventListener('error', onEarlyError);
  window.addEventListener('unhandledrejection', onEarlyRejection);

  import(/* webpackChunkName: "monitoring" */ './sentryClient')
    .then(({ startSentry }) => {
      client = startSentry({
        dsn: DSN,
        tunnel: `${API_BASE_URL}/api/monitoring/tunnel`,
        environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
        release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
      });
      // The SDK's own global handlers take over from here.
      window.removeEventListener('error', onEarlyError);
      window.removeEventListener('unhandledrejection', onEarlyRejection);
      const queued = early;
      early = null;
      queued.forEach(([error, context]) => client.capture(error, context));
    })
    .catch(() => {
      // The chunk failed to load (offline, or blocked). Monitoring is a
      // convenience; the page carries on without it.
      window.removeEventListener('error', onEarlyError);
      window.removeEventListener('unhandledrejection', onEarlyRejection);
      early = null;
    });
}

// For errors the app catches itself -- ErrorBoundary is the main caller.
// Uncaught errors and rejections need no call; the SDK reports those.
export function reportError(error, context) {
  if (!DSN) return;
  if (client) client.capture(error, context);
  else hold(error, context);
}
